import '@angular/compiler';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import {
  CustomDateAdapter,
  MY_FORMATS,
} from '@core/helper/custom-date-adapter';

// Modules
import { AppRoutingModule } from './app-routing.module';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule } from '@angular/platform-browser';
import { MaterialCssVarsModule } from 'angular-material-css-vars';
import {
  APP_INITIALIZER,
  ErrorHandler,
  NgModule,
  provideZoneChangeDetection,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@environment';

// translate
import { TranslationLoaderModule } from '@shared/translation-loader/translation-loader.module';
import { TranslateLoader } from '@ngx-translate/core';
import { JsonTranslateHttpMultiLoader } from '@shared/translation-loader/lib/json.translate.http.multiLoader';
import { HttpClient } from '@angular/common/http';

// Services
import { ApiService } from '@services/api.service';
import {
  HTTP_INTERCEPTORS,
  provideHttpClient,
  withInterceptors,
  withXhr,
} from '@angular/common/http';
import { AuthInterceptor } from '@core/interceptors/auth.interceptor';
import { ErrorInterceptor } from '@core/interceptors/error.interceptor';
import { CookieService } from 'ngx-cookie-service';
import { AuthService } from '@services/auth.service';
import { ErrorModule } from '@shared/error/module';
import { ErrorService } from '@services/error.service';
import { NotificationModule } from '@shared/notification/module';
import { SnackBarModule } from '@shared/snack-bar/snack-bar-module';
import { HeaderModule } from '@shared/header/header.module';

import { AppComponent } from './app.component';
import { IconsModule } from '@shared/icons/icons.module';
import { JsonApiModule } from '@elearnio/angular2-jsonapi';
import { MatDialogModule } from '@angular/material/dialog';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import {
  NgHttpLoaderComponent,
  pendingRequestsInterceptor$,
} from 'ng-http-loader';
import { ElearnioLoaderModule } from '@shared/elearnio-loader/elearnio-loader.module';
import { MatSidenavModule } from '@angular/material/sidenav';
import { AppSidebarModule } from '@shared/app-sidebar/app-sidebar.module';
import { AppToolbarModule } from '@app/shared/app-toolbar/app-toolbar.module';
import { NavigationSidebarModule } from '@shared/navigation-sidebar/navigation-sidebar.module';
import { appInitializer } from '@core/app.initializer';
import { provideRouter, Router } from '@angular/router';
import { MsalModule, MsalService, MSAL_INSTANCE } from '@azure/msal-angular';
import {
  IPublicClientApplication,
  PublicClientApplication,
} from '@azure/msal-browser';
import { CompanySettingService } from '@services/company-setting.service';
import { EditCurrentPasswordDialogModule } from '@shared/edit-current-password/edit-current-password-dialog.module';
import { routes } from './app.routes';
import { TranslationHelper } from './core/helper/translation.helper';
import { AiElearnioLoaderModule } from './shared/ai-elearnio-loader/ai-elearnio-loader.module';
import { CourseActivity } from '@models/course/course-activity.model';
import { Injector } from '@angular/core';
import { ZoomRedirectComponent } from './core/components/zoom-redirect/zoom-redirect.component';
import { ConfirmDialogModule } from '@shared/confirm-dialog/confirm-dialog.module';

export const appImports = [
  BrowserModule,
  BrowserAnimationsModule,
  TranslationLoaderModule,
  AppRoutingModule,
  JsonApiModule,
  ErrorModule,
  NotificationModule,
  SnackBarModule,
  HeaderModule,
  MatDialogModule,
  IconsModule,
  MaterialCssVarsModule.forRoot(),
  MatProgressBarModule,
  FlexLayoutModule,
  NgHttpLoaderComponent,
  MatSidenavModule,
  AppSidebarModule,
  AppToolbarModule,
  MsalModule,
  EditCurrentPasswordDialogModule,
  ConfirmDialogModule,
  NavigationSidebarModule,
  AiElearnioLoaderModule,
  ElearnioLoaderModule,
  ZoomRedirectComponent,
];

export function MSALInstanceFactory(
  companySettingService: CompanySettingService,
): IPublicClientApplication {
  const company = companySettingService.getCompany();
  const msIntegration = company?.msTeamsIntegration;
  const clientId = msIntegration?.api_client_id || 'invalid';
  const tenantId = msIntegration?.api_tenant_id || 'invalid';

  return new PublicClientApplication({
    auth: {
      clientId,
      authority: `https://login.microsoftonline.com/${tenantId}`,
    },
  });
}

export function initTranslationHelper(translate: TranslateService) {
  return () => TranslationHelper.init(translate);
}

export function initCourseActivityInjector(injector: Injector) {
  return () => {
    CourseActivity.setInjector(injector);
  };
}

@NgModule({
  declarations: [AppComponent],
  imports: [...appImports],
  providers: [
    {
      provide: TranslateLoader,
      useFactory: JsonTranslateHttpMultiLoader.factory,
      deps: [HttpClient],
    },
    {
      provide: ErrorHandler,
      useClass: ErrorService,
    },
    {
      provide: MSAL_INSTANCE,
      deps: [CompanySettingService],
      useFactory: MSALInstanceFactory,
    },
    MsalService,
    ApiService,
    CookieService,
    provideZoneChangeDetection(),
    provideRouter(routes),
    // Angular v22 changed the default HttpBackend to `FetchBackend`, which runs
    // outside NgZone. `withXhr()` restores the pre-v22 XHR backend so HTTP
    // responses are delivered inside Angular's zone again (see PR
    // angular/angular#58212). Datastore-level zone re-entry is handled in the
    // `Datastore` service, whose async delivery detaches from the zone.
    provideHttpClient(
      withXhr(),
      withInterceptors([pendingRequestsInterceptor$]),
    ),
    {
      provide: APP_INITIALIZER,
      useFactory: appInitializer,
      multi: true,
      deps: [AuthService, Router],
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ErrorInterceptor,
      multi: true,
    },
    {
      provide: 'googleTagManagerId',
      useValue: environment.googleTagManagerId,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initTranslationHelper,
      deps: [TranslateService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initCourseActivityInjector,
      deps: [Injector],
      multi: true,
    },
    {
      provide: DateAdapter,
      useClass: CustomDateAdapter,
      deps: [MAT_DATE_LOCALE, TranslateService],
    },
    { provide: MAT_DATE_FORMATS, useValue: MY_FORMATS },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
